import {
  DeckOperation,
  TabletopState,
  AnimationState,
  CardWithAnimation,
  DiceState,
  DeckState,
  CardState,
  HandCardState,
  Position,
  TokenState,
  CoinState,
  DeckInsertionPosition
} from '../tabletop/types';
import { drawCards, drawCardsFromBottom, shuffleCards, cutDeck } from '../deck/deckUtils';

export type TabletopEvent =
  | { type: 'notification'; level: 'success' | 'error' | 'info'; message: string }
  | { type: 'history'; message: string };

export interface TabletopActionResult {
  state: TabletopState;
  events: TabletopEvent[];
}

export interface DeckOperationContext {
  createDealAnimation?: (from: Position, to: Position, duration: number) => AnimationState;
  currentPlayerId?: string | null;
}

export interface TabletopActionContext extends DeckOperationContext {
  createFlipAnimation?: () => AnimationState;
  createRotateAnimation?: (from: number, to: number) => AnimationState;
  generateId?: () => string;
  randomInt?: (sides: number) => number;
  currentPlayerId?: string | null;
}

export type TabletopAction =
  | { type: 'deckOperation'; deckId: string; operation: DeckOperation }
  | { type: 'flipCard'; cardId: string }
  | { type: 'rotateCard'; cardId: string; rotation: number }
  | { type: 'moveCardToHand'; cardId: string }
  | { type: 'flipHandCard'; cardId: string }
  | { type: 'setHandCardSelected'; cardId: string; selected: boolean }
  | { type: 'moveTableItem'; itemType: 'card' | 'deck' | 'dice' | 'token' | 'coin'; id: string; position: Position }
  | { type: 'moveCardGroup'; cardIds: string[]; positions: Record<string, Position> }
  | { type: 'rotateDeck'; deckId: string; rotation: number }
  | { type: 'removeTableItem'; itemType: 'card' | 'deck' | 'dice' | 'token' | 'coin'; id: string }
  | { type: 'importDeck'; deck: DeckState }
  | {
      type: 'placeHandCard';
      cardId: string;
      position: Position;
      faceUp?: boolean;
      rotation?: number;
      suppressLog?: boolean;
    }
  | { type: 'addCardToDeck'; cardId: string; deckId: string; position?: DeckInsertionPosition }
  | { type: 'addTestCard'; id?: string; position: Position }
  | { type: 'addTestDice'; id?: string; position: Position }
  | { type: 'rollDice'; diceId: string }
  | { type: 'addToken'; id?: string; position: Position; text?: string; color?: string }
  | { type: 'updateToken'; id: string; text?: string; color?: string }
  | { type: 'addCoin'; id?: string; position: Position; side?: 'heads' | 'tails' }
  | { type: 'flipCoin'; coinId: string }
  | {
      type: 'moveDeckCardsToTable';
      deckId: string;
      cardIds: string[];
      position: Position;
      faceUp?: boolean;
      rotation?: number;
    }
  | {
      type: 'moveDeckCardsToHand';
      deckId: string;
      cardIds: string[];
    }
  | {
      type: 'moveDeckCardsToDeck';
      sourceDeckId: string;
      targetDeckId: string;
      cardIds: string[];
      position?: DeckInsertionPosition;
    }
  | { type: 'recordHistoryEntry'; message: string };

const DEAL_BASE_DURATION = 400;
const DEAL_DURATION_STEP = 50;
const DEFAULT_CARD_Z_INDEX = 10;
const DEFAULT_PROP_Z_INDEX = 1000;

const toRadians = (rotation: number): number => (rotation * Math.PI) / 180;

const getHistoryCardReference = (name?: string, revealName: boolean = false): string => {
  if (!revealName) {
    return 'a face-down card';
  }

  return name ? `"${name}"` : 'a card';
};

const getRotationVector = (
  rotation: number,
  axis: 'right' | 'down'
): Position => {
  const radians = toRadians(rotation);
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);

  if (axis === 'right') {
    return { x: cos, y: sin };
  }

  return { x: -sin, y: cos };
};

const cloneCardForTable = (
  card: CardState,
  position: Position,
  parentDeckId: string,
  animation?: AnimationState
): CardWithAnimation => ({
  ...card,
  position,
  parentDeckId,
  animation
});

const getCardStackingOrder = (card: CardState): number =>
  typeof card.zIndex === 'number' ? card.zIndex : DEFAULT_CARD_Z_INDEX;

const getNextCardZIndex = (cards: Record<string, CardState>): number =>
  Object.values(cards).reduce(
    (maxZIndex, card) => Math.max(maxZIndex, getCardStackingOrder(card)),
    DEFAULT_CARD_Z_INDEX - 1
  ) + 1;

const getPropStackingOrder = (item: { zIndex?: number }): number =>
  typeof item.zIndex === 'number' ? item.zIndex : DEFAULT_PROP_Z_INDEX;

const getNextPropZIndex = (state: TabletopState): number => {
  const dice = Object.values(state.dice ?? {});
  const tokens = Object.values(state.tokens ?? {});
  const coins = Object.values(state.coins ?? {});

  return [...dice, ...tokens, ...coins].reduce(
    (maxZIndex, item) => Math.max(maxZIndex, getPropStackingOrder(item)),
    DEFAULT_PROP_Z_INDEX - 1
  ) + 1;
};

export function applyDeckOperation(
  state: TabletopState,
  deckId: string,
  operation: DeckOperation,
  context: DeckOperationContext = {}
): TabletopActionResult {
  const deck = state.decks[deckId];
  if (!deck) {
    return { state, events: [] };
  }

  switch (operation.type) {
    case 'draw': {
      const { deck: updatedDeck, drawnCards } = drawCards(deck, operation.count);

      if (drawnCards.length === 0) {
        return {
          state,
          events: [{ type: 'notification', level: 'info', message: `"${deck.name}" has no cards to draw` }]
        };
      }

      const nextCards = { ...state.cards };
      const baseZIndex = getNextCardZIndex(state.cards);
      drawnCards.forEach((card, index) => {
        const finalPosition = {
          x: deck.position.x + 100 + index * 20,
          y: deck.position.y + 20
        };
        const duration = DEAL_BASE_DURATION + index * DEAL_DURATION_STEP;
        const animation = context.createDealAnimation
          ? context.createDealAnimation(deck.position, finalPosition, duration)
          : undefined;
        nextCards[card.id] = {
          ...cloneCardForTable(card, finalPosition, deckId, animation),
          zIndex: baseZIndex + index
        };
      });

      const count = drawnCards.length;
      const plural = count === 1 ? 'card' : 'cards';

      return {
        state: {
          ...state,
          cards: nextCards,
          decks: {
            ...state.decks,
            [deckId]: updatedDeck
          }
        },
        events: [{ type: 'history', message: `Drew ${count} ${plural} from "${deck.name}" to table` }]
      };
    }

    case 'drawBelow': {
      const dropPosition = operation.position ?? deck.position;
      const { deck: updatedDeck, drawnCards } = drawCards(deck, operation.count, dropPosition);

      if (drawnCards.length === 0) {
        return {
          state,
          events: [{ type: 'notification', level: 'info', message: `"${deck.name}" has no cards to draw` }]
        };
      }

      const nextCards = { ...state.cards };
      const rightVector = getRotationVector(deck.rotation, 'right');
      const downVector = getRotationVector(deck.rotation, 'down');
      const overlapOffsetX = rightVector.x * 6 + downVector.x * 8;
      const overlapOffsetY = rightVector.y * 6 + downVector.y * 8;
      const stackOffsetX = rightVector.x * 2 + downVector.x * 2;
      const stackOffsetY = rightVector.y * 2 + downVector.y * 2;
      const baseZIndex = 5;

      drawnCards.forEach((card, index) => {
        const positionedCard: CardState = {
          ...card,
          position: {
            x: dropPosition.x + overlapOffsetX + index * stackOffsetX,
            y: dropPosition.y + overlapOffsetY + index * stackOffsetY
          },
          parentDeckId: deckId,
          zIndex: Math.max(1, baseZIndex - index)
        };
        nextCards[positionedCard.id] = positionedCard;
      });

      const count = drawnCards.length;
      const plural = count === 1 ? 'card' : 'cards';

      return {
        state: {
          ...state,
          cards: nextCards,
          decks: {
            ...state.decks,
            [deckId]: updatedDeck
          }
        },
        events: [
          { type: 'notification', level: 'success', message: `Placed ${count} ${plural} under "${deck.name}"` },
          { type: 'history', message: `Drew ${count} ${plural} from "${deck.name}" below the deck` }
        ]
      };
    }

    case 'drawFromBottom': {
      const { deck: updatedDeck, drawnCards } = drawCardsFromBottom(deck, operation.count);

      if (drawnCards.length === 0) {
        return {
          state,
          events: [{ type: 'notification', level: 'info', message: `"${deck.name}" has no cards to draw` }]
        };
      }

      const nextCards = { ...state.cards };
      const baseZIndex = getNextCardZIndex(state.cards);
      drawnCards.forEach((card, index) => {
        const finalPosition = {
          x: deck.position.x + 100 + index * 20,
          y: deck.position.y + 20
        };
        const duration = DEAL_BASE_DURATION + index * DEAL_DURATION_STEP;
        const animation = context.createDealAnimation
          ? context.createDealAnimation(deck.position, finalPosition, duration)
          : undefined;
        nextCards[card.id] = {
          ...cloneCardForTable(card, finalPosition, deckId, animation),
          zIndex: baseZIndex + index
        };
      });

      const count = drawnCards.length;
      const plural = count === 1 ? 'card' : 'cards';

      return {
        state: {
          ...state,
          cards: nextCards,
          decks: {
            ...state.decks,
            [deckId]: updatedDeck
          }
        },
        events: [{ type: 'history', message: `Drew ${count} ${plural} from bottom of "${deck.name}" to table` }]
      };
    }

    case 'drawToHand':
    case 'drawFromBottomToHand': {
      const drawFn = operation.type === 'drawToHand' ? drawCards : drawCardsFromBottom;
      const { deck: updatedDeck, drawnCards } = drawFn(deck, operation.count);

      if (drawnCards.length === 0) {
        return {
          state,
          events: [{ type: 'notification', level: 'info', message: `"${deck.name}" has no cards to draw` }]
        };
      }

      const nextHand: Record<string, HandCardState> = { ...state.hand };
      const nextOrder = [...(state.handOrder || [])];

      const ownerId = context.currentPlayerId ?? null;
      drawnCards.forEach(card => {
        nextHand[card.id] = {
          id: card.id,
          imageUrl: card.imageUrl,
          backImageUrl: card.backImageUrl,
          name: card.name,
          owner: ownerId ?? card.owner ?? 'local',
          parentDeckId: deckId,
          isRevealedToOthers: false
        };
        nextOrder.push(card.id);
      });

      const count = drawnCards.length;
      const plural = count === 1 ? 'card' : 'cards';

      return {
        state: {
          ...state,
          hand: nextHand,
          handOrder: nextOrder,
          decks: {
            ...state.decks,
            [deckId]: updatedDeck
          }
        },
        events: [
          { type: 'notification', level: 'success', message: `Drew ${count} ${plural} to hand` },
          { type: 'history', message: `Drew ${count} ${plural} from "${deck.name}" to hand` }
        ]
      };
    }

    case 'shuffle': {
      const shuffledDeck = shuffleCards({ ...deck });
      const updatedDeck = {
        ...shuffledDeck,
        lastShuffledAt: Date.now()
      };
      return {
        state: {
          ...state,
          decks: {
            ...state.decks,
            [deckId]: updatedDeck
          }
        },
        events: [
          {
            type: 'notification',
            level: 'success',
            message: `Shuffled "${updatedDeck.name}" (${updatedDeck.cards.length} cards)`
          },
          { type: 'history', message: `Shuffled "${deck.name}"` }
        ]
      };
    }

    case 'cut': {
      const position = operation.position !== undefined ? operation.position : 0.5;
      const updatedDeck = cutDeck({ ...deck }, position);
      return {
        state: {
          ...state,
          decks: {
            ...state.decks,
            [deckId]: updatedDeck
          }
        },
        events: [
          { type: 'notification', level: 'success', message: `Cut "${updatedDeck.name}"` },
          { type: 'history', message: `Cut "${deck.name}"` }
        ]
      };
    }

    case 'mulligan': {
      const tableEntries = Object.entries(state.cards).filter(([, card]) => card.parentDeckId === deckId);
      const handEntries = Object.entries(state.hand || {}).filter(([, card]) => card.parentDeckId === deckId);
      if (tableEntries.length === 0 && handEntries.length === 0) {
        return {
          state,
          events: [{ type: 'notification', level: 'success', message: 'No cards to mulligan into deck' }]
        };
      }

      const nextCards = { ...state.cards };
      tableEntries.forEach(([cardId]) => {
        delete nextCards[cardId];
      });

      const nextHand: Record<string, HandCardState> = { ...(state.hand || {}) };
      const handIdsToRemove = new Set<string>();
      handEntries.forEach(([cardId]) => {
        delete nextHand[cardId];
        handIdsToRemove.add(cardId);
      });

      const nextHandOrder = (state.handOrder || []).filter(cardId => !handIdsToRemove.has(cardId));

      const returnedTableCards: CardState[] = tableEntries.map(([, card]) => {
        const { parentDeckId: _discarded, animation, ...rest } = card as CardWithAnimation;
        return rest;
      });

      const returnedHandCards: CardState[] = handEntries.map(([, card]) => ({
        id: card.id,
        position: { ...deck.position },
        rotation: deck.rotation,
        isFaceUp: false,
        imageUrl: card.imageUrl,
        backImageUrl: card.backImageUrl,
        name: card.name,
        owner: card.owner
      }));

      const returnedCards = [...returnedTableCards, ...returnedHandCards];
      const updatedDeck = {
        ...deck,
        cards: [...deck.cards, ...returnedCards]
      };

      const count = returnedCards.length;
      const plural = count === 1 ? 'card' : 'cards';

      return {
        state: {
          ...state,
          cards: nextCards,
          hand: nextHand,
          handOrder: nextHandOrder,
          decks: {
            ...state.decks,
            [deckId]: updatedDeck
          }
        },
        events: [
          { type: 'notification', level: 'success', message: `Mulliganed ${count} ${plural} into deck` },
          { type: 'history', message: `Mulliganed ${count} ${plural} into "${deck.name}"` }
        ]
      };
    }

    default:
      // Log warning for unhandled deck operations to catch bugs early
      console.warn(
        `⚠️ Unhandled deck operation type in applyDeckOperation: "${operation.type}"`,
        'This operation may be missing a case handler.',
        operation
      );
      return { state, events: [] };
  }
}

const DEFAULT_TEST_CARD = {
  imageUrl: 'https://cards.scryfall.io/normal/front/4/e/4ef4d7f0-dda2-4171-a7f1-8c40eb2f254c.jpg',
  backImageUrl: 'https://cards.scryfall.io/normal/back/4/e/4ef4d7f0-dda2-4171-a7f1-8c40eb2f254c.jpg',
  name: 'Test Card'
};

const DEFAULT_TEST_DICE: Omit<DiceState, 'id'> = {
  position: { x: 0, y: 0 },
  rotation: 0,
  value: 1,
  sides: 6,
  color: '#ff0000',
  size: 50,
  rolling: false,
  rollId: 0
};

const DEFAULT_COIN: Omit<CoinState, 'id'> = {
  position: { x: 0, y: 0 },
  rotation: 0,
  side: 'heads',
  size: 58,
  flipping: false,
  flipId: 0
};

export function applyTabletopAction(
  state: TabletopState,
  action: TabletopAction,
  context: TabletopActionContext = {}
): TabletopActionResult {
  switch (action.type) {
    case 'deckOperation':
      return applyDeckOperation(state, action.deckId, action.operation, {
        createDealAnimation: context.createDealAnimation,
        currentPlayerId: context.currentPlayerId
      });

    case 'moveTableItem': {
      const { itemType, id, position } = action;

      if (itemType === 'card') {
        const card = state.cards[id];
        if (!card) return { state, events: [] };

        return {
          state: {
            ...state,
            cards: {
              ...state.cards,
              [id]: {
                ...card,
                position,
                zIndex: getNextCardZIndex(state.cards)
              }
            }
          },
          events: []
        };
      }

      if (itemType === 'deck') {
        const deck = state.decks[id];
        if (!deck) return { state, events: [] };

        return {
          state: {
            ...state,
            decks: {
              ...state.decks,
              [id]: {
                ...deck,
                position
              }
            }
          },
          events: []
        };
      }

      if (itemType === 'dice') {
        const dice = state.dice[id];
        if (!dice) return { state, events: [] };

        const zIndex = getNextPropZIndex(state);

        return {
          state: {
            ...state,
            dice: {
              ...state.dice,
              [id]: {
                ...dice,
                position,
                zIndex
              }
            }
          },
          events: []
        };
      }

      if (itemType === 'token') {
        const token = state.tokens[id];
        if (!token) return { state, events: [] };

        const zIndex = getNextPropZIndex(state);

        return {
          state: {
            ...state,
            tokens: {
              ...state.tokens,
              [id]: {
                ...token,
                position,
                zIndex
              }
            }
          },
          events: []
        };
      }

      if (itemType === 'coin') {
        const coin = state.coins?.[id];
        if (!coin) return { state, events: [] };

        const zIndex = getNextPropZIndex(state);

        return {
          state: {
            ...state,
            coins: {
              ...(state.coins ?? {}),
              [id]: {
                ...coin,
                position,
                zIndex
              }
            }
          },
          events: []
        };
      }

      return { state, events: [] };
    }

    case 'moveCardGroup': {
      const orderedCardIds = action.cardIds.filter(cardId => {
        return Boolean(state.cards[cardId] && action.positions[cardId]);
      });

      if (orderedCardIds.length === 0) {
        return { state, events: [] };
      }

      const nextCards = { ...state.cards };
      const baseZIndex = getNextCardZIndex(state.cards);

      orderedCardIds.forEach((cardId, index) => {
        const card = state.cards[cardId];
        const position = action.positions[cardId];
        if (!card || !position) {
          return;
        }

        nextCards[cardId] = {
          ...card,
          position,
          zIndex: baseZIndex + index
        };
      });

      return {
        state: {
          ...state,
          cards: nextCards
        },
        events: []
      };
    }

    case 'rotateDeck': {
      const deck = state.decks[action.deckId];
      if (!deck) return { state, events: [] };

      return {
        state: {
          ...state,
          decks: {
            ...state.decks,
            [action.deckId]: {
              ...deck,
              rotation: action.rotation
            }
          }
        },
        events: []
      };
    }

    case 'removeTableItem': {
      const { itemType, id } = action;
      if (itemType === 'card') {
        const card = state.cards[id];
        if (!card) return { state, events: [] };

        const nextCards = { ...state.cards };
        delete nextCards[id];

        const cardReference = getHistoryCardReference(card.name, card.isFaceUp);

        return {
          state: {
            ...state,
            cards: nextCards
          },
          events: [
            { type: 'history', message: `Removed ${cardReference} from tabletop` }
          ]
        };
      }

      if (itemType === 'deck') {
        const deck = state.decks[id];
        if (!deck) return { state, events: [] };

        const nextDecks = { ...state.decks };
        delete nextDecks[id];

        return {
          state: {
            ...state,
            decks: nextDecks
          },
          events: [
            { type: 'history', message: `Removed deck "${deck.name}"` }
          ]
        };
      }

      if (itemType === 'dice') {
        const dice = state.dice[id];
        if (!dice) return { state, events: [] };

        const nextDice = { ...state.dice };
        delete nextDice[id];

        return {
          state: {
            ...state,
            dice: nextDice
          },
          events: [
            { type: 'history', message: `Removed d${dice.sides} dice` }
          ]
        };
      }

      if (itemType === 'token') {
        const token = state.tokens[id];
        if (!token) return { state, events: [] };

        const nextTokens = { ...state.tokens };
        delete nextTokens[id];

        return {
          state: {
            ...state,
            tokens: nextTokens
          },
          events: [
            { type: 'history', message: `Removed token "${token.text}"` }
          ]
        };
      }

      if (itemType === 'coin') {
        const coin = state.coins?.[id];
        if (!coin) return { state, events: [] };

        const nextCoins = { ...(state.coins ?? {}) };
        delete nextCoins[id];

        return {
          state: {
            ...state,
            coins: nextCoins
          },
          events: [
            { type: 'history', message: `Removed ${coin.side} coin` }
          ]
        };
      }

      return { state, events: [] };
    }

    case 'importDeck': {
      const deck = action.deck;
      if (!deck.id) {
        return { state, events: [] };
      }

      const cardCount = deck.cards?.length ?? 0;
      const plural = cardCount === 1 ? 'card' : 'cards';

      const clonedCards = (deck.cards ?? []).map(card => ({ ...card }));
      const nextDeck: DeckState = {
        ...deck,
        cards: clonedCards
      };

      return {
        state: {
          ...state,
          decks: {
            ...state.decks,
            [deck.id]: nextDeck
          }
        },
        events: [
          {
            type: 'notification',
            level: 'success',
            message: `Imported deck "${deck.name ?? 'Deck'}" with ${cardCount} ${plural}`
          },
          {
            type: 'history',
            message: `Imported deck "${deck.name ?? 'Deck'}" (${cardCount} ${plural})`
          }
        ]
      };
    }

    case 'flipCard': {
      const card = state.cards[action.cardId];
      if (!card) return { state, events: [] };

      const nextIsFaceUp = !card.isFaceUp;
      const updatedCard: CardWithAnimation = {
        ...card,
        isFaceUp: nextIsFaceUp
      };

      const animation = context.createFlipAnimation?.();
      if (animation) {
        updatedCard.animation = animation;
      } else if ('animation' in updatedCard) {
        delete updatedCard.animation;
      }

      return {
        state: {
          ...state,
          cards: {
            ...state.cards,
            [action.cardId]: updatedCard
          }
        },
        events: [
          {
            type: 'history',
            message: nextIsFaceUp
              ? `Turned ${getHistoryCardReference(card.name, true)} face up on tabletop`
              : `Turned ${getHistoryCardReference(card.name, card.isFaceUp)} face down on tabletop`
          }
        ]
      };
    }

    case 'rotateCard': {
      const card = state.cards[action.cardId];
      if (!card) return { state, events: [] };

      const updatedCard: CardWithAnimation = {
        ...card,
        rotation: action.rotation
      };

      const animation = context.createRotateAnimation?.(card.rotation, action.rotation);
      if (animation) {
        updatedCard.animation = animation;
      } else if ('animation' in updatedCard) {
        delete updatedCard.animation;
      }

      return {
        state: {
          ...state,
          cards: {
            ...state.cards,
            [action.cardId]: updatedCard
          }
        },
        events: []
      };
    }

    case 'moveCardToHand': {
      const card = state.cards[action.cardId];
      if (!card) return { state, events: [] };

      const nextCards = { ...state.cards };
      delete nextCards[action.cardId];

      const nextHand: Record<string, HandCardState> = { ...(state.hand || {}) };
      nextHand[action.cardId] = {
        id: card.id,
        imageUrl: card.imageUrl,
        backImageUrl: card.backImageUrl,
        name: card.name,
        owner: context.currentPlayerId ?? card.owner ?? 'local',
        parentDeckId: card.parentDeckId,
        isRevealedToOthers: false
      };

      const nextHandOrder = [...(state.handOrder || []), action.cardId];
      const cardName = card.name || 'Card';
      const wasFaceUp = Boolean(card.isFaceUp);
      const notificationMessage = wasFaceUp ? `Moved "${cardName}" to hand` : 'Moved a face-down card to hand';
      const historyMessage = wasFaceUp
        ? `Moved "${cardName}" from tabletop to hand`
        : 'Moved a face-down card from tabletop to hand';

      return {
        state: {
          ...state,
          cards: nextCards,
          hand: nextHand,
          handOrder: nextHandOrder
        },
        events: [
          { type: 'notification', level: 'success', message: notificationMessage },
          { type: 'history', message: historyMessage }
        ]
      };
    }

    case 'flipHandCard': {
      const handCard = state.hand[action.cardId];
      if (!handCard) return { state, events: [] };

      const ownerId = handCard.owner ?? 'local';
      const actorId = context.currentPlayerId ?? 'local';
      if (ownerId !== actorId) {
        return { state, events: [] };
      }

      const updatedCard: HandCardState = {
        ...handCard,
        isRevealedToOthers: !handCard.isRevealedToOthers
      };

      return {
        state: {
          ...state,
          hand: {
            ...state.hand,
            [action.cardId]: updatedCard
          }
        },
        events: [
          {
            type: 'history',
            message: updatedCard.isRevealedToOthers
              ? `Revealed ${handCard.name ? `"${handCard.name}"` : 'a card'} from hand`
              : `Stopped revealing ${handCard.name ? `"${handCard.name}"` : 'a card'} from hand`
          }
        ]
      };
    }

    case 'setHandCardSelected': {
      const handCard = state.hand[action.cardId];
      if (!handCard) return { state, events: [] };

      const actorId = context.currentPlayerId ?? 'local';
      const existing = handCard.selectedBy ?? [];
      const alreadySelected = existing.includes(actorId);

      if (action.selected && alreadySelected) {
        return { state, events: [] };
      }

      if (!action.selected && !alreadySelected) {
        return { state, events: [] };
      }

      let nextSelected = existing;
      if (action.selected) {
        nextSelected = [...existing, actorId];
      } else {
        nextSelected = existing.filter(id => id !== actorId);
      }

      const updatedCard: HandCardState = {
        ...handCard
      };

      if (nextSelected.length > 0) {
        updatedCard.selectedBy = nextSelected;
      } else if (updatedCard.selectedBy) {
        delete updatedCard.selectedBy;
      }

      return {
        state: {
          ...state,
          hand: {
            ...state.hand,
            [action.cardId]: updatedCard
          }
        },
        events: []
      };
    }

    case 'placeHandCard': {
      const handCard = state.hand[action.cardId];
      if (!handCard) return { state, events: [] };

      const actorId = context.currentPlayerId ?? null;
      if (handCard.owner && actorId && handCard.owner !== actorId) {
        return { state, events: [] };
      }

      const { faceUp = true, rotation = 0, suppressLog = false } = action;

      const nextHand = { ...state.hand };
      delete nextHand[action.cardId];

      const nextOrder = (state.handOrder || []).filter(id => id !== action.cardId);

      const placedCard: CardState = {
        id: action.cardId,
        position: action.position,
        rotation,
        isFaceUp: faceUp,
        imageUrl: handCard.imageUrl,
        backImageUrl: handCard.backImageUrl,
        name: handCard.name,
        counters: {},
        owner: handCard.owner,
        parentDeckId: handCard.parentDeckId,
        zIndex: getNextCardZIndex(state.cards)
      };

      const events: TabletopEvent[] = [];
      const cardName = placedCard.name || 'Card';

      if (!suppressLog) {
        const isRevealed = placedCard.isFaceUp;
        if (isRevealed) {
          events.push({ type: 'notification', level: 'success', message: `Played "${cardName}" from hand` });
          events.push({ type: 'history', message: `Played "${cardName}" from hand to tabletop` });
        } else {
          events.push({ type: 'notification', level: 'success', message: 'Placed a face-down card from hand' });
          events.push({ type: 'history', message: 'Placed a face-down card from hand to tabletop' });
        }
      }

      return {
        state: {
          ...state,
          hand: nextHand,
          handOrder: nextOrder,
          cards: {
            ...state.cards,
            [action.cardId]: placedCard
          }
        },
        events
      };
    }

    case 'addCardToDeck': {
      const card = state.cards[action.cardId];
      const deck = state.decks[action.deckId];
      if (!card || !deck) return { state, events: [] };

      const insertionPosition: DeckInsertionPosition = action.position ?? 'top';
      const cards =
        insertionPosition === 'bottom'
          ? [card, ...deck.cards]
          : [...deck.cards, card];

      const updatedDeck = {
        ...deck,
        cards
      };

      const nextCards = { ...state.cards };
      delete nextCards[action.cardId];

      const notificationMessage =
        insertionPosition === 'bottom'
          ? `Returned a card to the bottom of "${deck.name}"`
          : `Returned a card to the top of "${deck.name}"`;
      const historyMessage =
        insertionPosition === 'bottom'
          ? `Returned a card to the bottom of "${deck.name}"`
          : `Returned a card to the top of "${deck.name}"`;

      return {
        state: {
          ...state,
          cards: nextCards,
          decks: {
            ...state.decks,
            [action.deckId]: updatedDeck
          }
        },
        events: [
          { type: 'notification', level: 'success', message: notificationMessage },
          { type: 'history', message: historyMessage }
        ]
      };
    }

    case 'addTestCard': {
      const id = action.id ?? context.generateId?.();
      if (!id) {
        return { state, events: [] };
      }

      return {
        state: {
          ...state,
          cards: {
            ...state.cards,
            [id]: {
              id,
              position: action.position,
              rotation: 0,
              isFaceUp: true,
              imageUrl: DEFAULT_TEST_CARD.imageUrl,
              backImageUrl: DEFAULT_TEST_CARD.backImageUrl,
              name: DEFAULT_TEST_CARD.name,
              counters: {},
              zIndex: getNextCardZIndex(state.cards)
            }
          }
        },
        events: [{ type: 'history', message: 'Added test card to tabletop' }]
      };
    }

    case 'addTestDice': {
      const id = action.id ?? context.generateId?.();
      if (!id) {
        return { state, events: [] };
      }

      const dice: DiceState = {
        id,
        ...DEFAULT_TEST_DICE,
        position: action.position,
        zIndex: getNextPropZIndex(state)
      };

      return {
        state: {
          ...state,
          dice: {
            ...state.dice,
            [id]: dice
          }
        },
        events: [{ type: 'history', message: 'Added test dice to tabletop' }]
      };
    }

    case 'rollDice': {
      const dice = state.dice[action.diceId];
      if (!dice) return { state, events: [] };

      const randomInt = context.randomInt ?? ((sides: number) => Math.floor(Math.random() * sides) + 1);
      const value = randomInt(dice.sides);

      const updatedDice: DiceState = {
        ...dice,
        value,
        rolling: true,
        rollId: (dice.rollId ?? 0) + 1
      };

      return {
        state: {
          ...state,
          dice: {
            ...state.dice,
            [action.diceId]: updatedDice
          }
        },
        events: [{ type: 'history', message: `Rolled d${dice.sides} → ${value}` }]
      };
    }

    case 'addToken': {
      const id = action.id ?? context.generateId?.();
      if (!id) {
        return { state, events: [] };
      }

      const text = action.text ?? '0';
      const color = action.color ?? '#ff6b6b';

      const token: TokenState = {
        id,
        position: action.position,
        rotation: 0,
        text,
        color,
        size: 60,
        zIndex: getNextPropZIndex(state)
      };

      return {
        state: {
          ...state,
          tokens: {
            ...state.tokens,
            [id]: token
          }
        },
        events: [{ type: 'history', message: `Added token "${text}"` }]
      };
    }

    case 'updateToken': {
      const token = state.tokens[action.id];
      if (!token) return { state, events: [] };

      const updatedToken: TokenState = {
        ...token
      };

      if (action.text !== undefined) {
        updatedToken.text = action.text;
      }
      if (action.color !== undefined) {
        updatedToken.color = action.color;
      }

      return {
        state: {
          ...state,
          tokens: {
            ...state.tokens,
            [action.id]: updatedToken
          }
        },
        events: []
      };
    }

    case 'addCoin': {
      const id = action.id ?? context.generateId?.();
      if (!id) {
        return { state, events: [] };
      }

      const coin: CoinState = {
        id,
        ...DEFAULT_COIN,
        position: action.position,
        side: action.side ?? DEFAULT_COIN.side,
        zIndex: getNextPropZIndex(state)
      };

      return {
        state: {
          ...state,
          coins: {
            ...(state.coins ?? {}),
            [id]: coin
          }
        },
        events: [{ type: 'history', message: `Added ${coin.side} coin to tabletop` }]
      };
    }

    case 'flipCoin': {
      const coin = state.coins?.[action.coinId];
      if (!coin) return { state, events: [] };

      const randomInt = context.randomInt ?? ((sides: number) => Math.floor(Math.random() * sides) + 1);
      const nextSide = randomInt(2) === 1 ? 'heads' : 'tails';
      const updatedCoin: CoinState = {
        ...coin,
        side: nextSide,
        flipping: true,
        flipId: (coin.flipId ?? 0) + 1
      };

      return {
        state: {
          ...state,
          coins: {
            ...(state.coins ?? {}),
            [action.coinId]: updatedCoin
          }
        },
        events: [{ type: 'history', message: `Flipped coin → ${nextSide}` }]
      };
    }

    case 'moveDeckCardsToTable': {
      const deck = state.decks[action.deckId];
      if (!deck || action.cardIds.length === 0) {
        return { state, events: [] };
      }

      const deckCardMap = new Map(deck.cards.map(card => [card.id, card]));
      const selectedCards = action.cardIds
        .map(cardId => deckCardMap.get(cardId))
        .filter((card): card is CardState => Boolean(card));

      if (selectedCards.length === 0) {
        return { state, events: [] };
      }

      const selectedIds = new Set(selectedCards.map(card => card.id));
      const remainingCards = deck.cards.filter(card => !selectedIds.has(card.id));

      const basePosition = action.position;
      const rotation = action.rotation ?? deck.rotation;
      const faceUp = action.faceUp ?? true;

      const nextCards = { ...state.cards };
      const baseZIndex = getNextCardZIndex(state.cards);
      const events: TabletopEvent[] = [];

      selectedCards.forEach((card, index) => {
        const cardId =
          context.generateId?.() ??
          `${card.id}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

        const placedCard: CardState = {
          id: cardId,
          position: {
            x: basePosition.x + index * 18,
            y: basePosition.y + index * 24
          },
          rotation,
          isFaceUp: faceUp,
          imageUrl: card.imageUrl,
          backImageUrl: card.backImageUrl,
          name: card.name,
          counters: card.counters ? { ...card.counters } : undefined,
          owner: context.currentPlayerId ?? card.owner,
          parentDeckId: deck.id,
          zIndex: baseZIndex + index
        };

        nextCards[cardId] = placedCard;
      });

      const count = selectedCards.length;
      const plural = count === 1 ? 'card' : 'cards';
      const revealNames = faceUp;
      const list = revealNames
        ? selectedCards.map(card => getHistoryCardReference(card.name, true)).join(', ')
        : '';

      events.push({
        type: 'history',
        message: revealNames
          ? `Moved ${count} ${plural} from "${deck.name}" to tabletop: ${list}`
          : `Moved ${count} ${plural} from "${deck.name}" to tabletop face down`
      });

      return {
        state: {
          ...state,
          cards: nextCards,
          decks: {
            ...state.decks,
            [deck.id]: {
              ...deck,
              cards: remainingCards
            }
          }
        },
        events
      };
    }

    case 'moveDeckCardsToHand': {
      const deck = state.decks[action.deckId];
      if (!deck || action.cardIds.length === 0) {
        return { state, events: [] };
      }

      const deckCardMap = new Map(deck.cards.map(card => [card.id, card]));
      const selectedCards = action.cardIds
        .map(cardId => deckCardMap.get(cardId))
        .filter((card): card is CardState => Boolean(card));

      if (selectedCards.length === 0) {
        return { state, events: [] };
      }

      const selectedIds = new Set(selectedCards.map(card => card.id));
      const remainingCards = deck.cards.filter(card => !selectedIds.has(card.id));

      const nextHand = { ...(state.hand || {}) };
      const nextOrder = [...(state.handOrder || [])];
      const events: TabletopEvent[] = [];

      selectedCards.forEach((card, index) => {
        const cardId =
          context.generateId?.() ??
          `${card.id}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

        const handCard: HandCardState = {
          id: cardId,
          imageUrl: card.imageUrl,
          backImageUrl: card.backImageUrl,
          name: card.name,
          owner: context.currentPlayerId ?? card.owner ?? 'local',
          parentDeckId: deck.id,
          isRevealedToOthers: false
        };

        nextHand[cardId] = handCard;
        nextOrder.push(cardId);
      });

      const count = selectedCards.length;
      const plural = count === 1 ? 'card' : 'cards';
      events.push({
        type: 'history',
        message: `Moved ${count} ${plural} from "${deck.name}" to hand`
      });

      return {
        state: {
          ...state,
          hand: nextHand,
          handOrder: nextOrder,
          decks: {
            ...state.decks,
            [deck.id]: {
              ...deck,
              cards: remainingCards
            }
          }
        },
        events
      };
    }

    case 'moveDeckCardsToDeck': {
      const sourceDeck = state.decks[action.sourceDeckId];
      const targetDeck = state.decks[action.targetDeckId];

      if (!sourceDeck || !targetDeck || action.cardIds.length === 0) {
        return { state, events: [] };
      }

      const sourceCardMap = new Map(sourceDeck.cards.map(card => [card.id, card]));
      const selectedCards = action.cardIds
        .map(cardId => sourceCardMap.get(cardId))
        .filter((card): card is CardState => Boolean(card));

      if (selectedCards.length === 0) {
        return { state, events: [] };
      }

      const insertionPosition: DeckInsertionPosition = action.position ?? 'top';
      const insertAtBottom = insertionPosition === 'bottom';

      const selectedIds = new Set(selectedCards.map(card => card.id));
      const remainingSourceCards = sourceDeck.cards.filter(card => !selectedIds.has(card.id));

      const clonedCards = selectedCards.map(card => ({
        ...card,
        parentDeckId: targetDeck.id
      }));

      const nextDecks = { ...state.decks };

      if (sourceDeck.id === targetDeck.id) {
        nextDecks[sourceDeck.id] = {
          ...sourceDeck,
          cards: insertAtBottom
            ? [...clonedCards, ...remainingSourceCards]
            : [...remainingSourceCards, ...clonedCards]
        };
      } else {
        nextDecks[sourceDeck.id] = {
          ...sourceDeck,
          cards: remainingSourceCards
        };
        nextDecks[targetDeck.id] = {
          ...targetDeck,
          cards: insertAtBottom
            ? [...clonedCards, ...targetDeck.cards]
            : [...targetDeck.cards, ...clonedCards]
        };
      }

      const count = selectedCards.length;
      const plural = count === 1 ? 'card' : 'cards';
      const destinationLabel =
        insertionPosition === 'bottom'
          ? `the bottom of "${targetDeck.name}"`
          : `the top of "${targetDeck.name}"`;

      return {
        state: {
          ...state,
          decks: nextDecks
        },
        events: [
          {
            type: 'history',
            message: `Moved ${count} ${plural} from "${sourceDeck.name}" to ${destinationLabel}`
          }
        ]
      };
    }

    case 'recordHistoryEntry': {
      return {
        state,
        events: [
          {
            type: 'history',
            message: action.message
          }
        ]
      };
    }

    default:
      // Log warning for unhandled action types to catch bugs early
      console.warn(
        `⚠️ Unhandled action type in applyTabletopAction: "${(action as TabletopAction).type}"`,
        'This action may be missing a case handler or placed in the wrong switch statement.',
        action
      );
      return { state, events: [] };
  }
}
